import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { ProjectDataTableItem } from '@kirin/api-client/contracts/projects';
import { queryKeys } from '@/hooks/api/queryKeys';
import { createProjectColumns } from './columns';
import { fetchProjectsDataTable } from './api';
import { PROJECTS_TABLE_CONFIG } from './constants';
import { ProjectUserListModal } from '../../components/ProjectUserListModal';

interface DataTableProjectsContentProps {
  organizationId: string;
}

export function DataTableProjectsContent({
  organizationId,
}: DataTableProjectsContentProps): ReactElement {
  const [selectedProject, setSelectedProject] =
    useState<ProjectDataTableItem | null>(null);

  const columns = useMemo(
    () => createProjectColumns(organizationId, setSelectedProject),
    [organizationId]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) =>
      queryKeys.projects.dataTable(organizationId, {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      }),
    [organizationId]
  );

  const queryConfig = useMemo(
    () => ({ staleTime: 30000, refetchOnWindowFocus: false }),
    []
  );

  const searchConfig = useMemo(
    () => ({ placeholder: 'Search projects...', className: 'w-[260px]' }),
    []
  );

  return (
    <>
      <DataTable<ProjectDataTableItem>
        id={PROJECTS_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the projects table"
        search={searchConfig}
        features={{ enableSorting: true }}
        queryFn={(params, signal) =>
          fetchProjectsDataTable(organizationId, params, signal)
        }
        getQueryKeysFromParams={getQueryKeysFromParams}
        noDataLabel="No projects found."
        queryConfig={queryConfig}
      />
      <ProjectUserListModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </>
  );
}

DataTableProjectsContent.displayName = 'DataTableProjectsContent';
